import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Plus, Check, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Multi-org switcher pinned at the top of the learner sidebar.
 *
 * - Shows the user's current org name + audience badge.
 * - Click → dropdown of all active memberships + "Add new org" link.
 * - Switching POSTs to /org/me/switch and full-reloads so all scoped reads
 *   refresh (dashboard, internal feed, etc.).
 */
const AUD_BADGE = {
  automotive:   { short: "AUTO",    classes: "bg-fc-red/15 text-fc-red border-fc-red/40" },
  business:     { short: "BIZ",     classes: "bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/40" },
  entrepreneur: { short: "FOUNDER", classes: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40" },
};

function AudBadge({ slug, testid }) {
  const meta = AUD_BADGE[slug];
  if (!meta) return null;
  return (
    <span
      className={`inline-flex items-center text-[8px] font-black tracking-[0.18em] uppercase border px-1 py-0.5 rounded ${meta.classes}`}
      data-testid={testid}
    >
      {meta.short}
    </span>
  );
}

export default function OrgSwitcher() {
  const [memberships, setMemberships] = useState([]);
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/org/me/memberships")
      .then((r) => {
        if (cancelled) return;
        const list = r.data.memberships || [];
        setMemberships(list);
        const currentId = r.data.current_organization_id;
        const cur = list.find((m) => m.organization.id === currentId) || list[0];
        setCurrent(cur);
      })
      .catch((e) => console.error("memberships load", e))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const switchTo = async (org_id) => {
    if (org_id === current?.organization?.id) { setOpen(false); return; }
    try {
      await api.post("/org/me/switch", { organization_id: org_id });
      toast.success("Switched organization");
      // Full reload to refresh every audience+org scoped surface
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Switch failed");
    }
  };

  if (loading || !current) {
    return (
      <div className="bg-[#060A24] border border-white/10 px-3 py-2 text-[11px] tracking-wide text-white/45" data-testid="org-switcher-loading">
        Loading org…
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left bg-[#060A24] border border-white/10 hover:border-fc-red/40 px-3 py-2.5 flex items-center gap-2 transition-colors"
        data-testid="org-switcher-btn"
      >
        <div className="w-7 h-7 bg-fc-red/15 flex items-center justify-center flex-shrink-0 text-fc-red">
          <Building2 size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[9px] tracking-[0.22em] uppercase font-bold text-white/45">CURRENT ORG</p>
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-[13px] font-bold truncate">{current.organization.name}</p>
            <AudBadge slug={current.organization.audience} testid="org-switcher-current-audience" />
          </div>
        </div>
        <ChevronDown size={13} className={`text-white/55 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-[#060A24] border border-white/15 shadow-2xl z-50 py-1 max-h-96 overflow-y-auto" data-testid="org-switcher-dropdown">
          {memberships.map((m) => {
            const isCurrent = m.organization.id === current.organization.id;
            return (
              <button
                key={m.organization.id}
                type="button"
                onClick={() => switchTo(m.organization.id)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors ${isCurrent ? "bg-fc-red/10" : ""}`}
                data-testid={`org-switcher-option-${m.organization.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <p className="text-[13px] font-bold truncate">{m.organization.name}</p>
                    <AudBadge slug={m.organization.audience} />
                  </div>
                  <p className="text-[10px] tracking-[0.18em] uppercase text-white/45">
                    {m.role_in_org}
                  </p>
                </div>
                {isCurrent && <Check size={13} className="text-fc-red flex-shrink-0" />}
              </button>
            );
          })}
          <div className="border-t border-white/10 mt-1 pt-1">
            <Link
              to="/register?step=1"
              className="flex items-center gap-2 px-3 py-2.5 text-[12px] tracking-wide text-fc-red hover:bg-fc-red/10 transition-colors"
              data-testid="org-switcher-add"
            >
              <Plus size={12} /> Add new organization
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
